import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash } from "@phosphor-icons/react";

function formatApiError(detail) {
  if (detail == null) return "Terjadi kesalahan. Silakan coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden">
      {/* Full-screen background */}
      <img
        src="/login-bg-shrimp.jpg"
        alt="Background"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#1A1A1A]/70 backdrop-blur-[2px]" />

      {/* Decorative circles */}
      <div className="absolute top-[-15%] right-[-10%] w-[500px] h-[500px] rounded-full bg-[#B8860B]/5 pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-8%] w-[400px] h-[400px] rounded-full bg-[#008080]/5 pointer-events-none" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-white/20">
          {/* Header with logo */}
          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] px-8 pt-10 pb-8 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#B8860B]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#008080]/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            <div className="relative">
              <img
                src="/logo-p3uw.png"
                alt="P3UW"
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover mx-auto shadow-2xl border-2 border-[#B8860B]/30 mb-4"
              />
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: "Work Sans" }}>
                P3UW
              </h1>
              <p className="text-[#B8860B] text-sm font-medium mt-1">
                Koperasi Petambak Bumi Dipasena
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Sistem Manajemen Operasional & Keuangan
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="px-8 py-8">
            {error && (
              <div
                data-testid="login-error"
                className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="administrator@p3uw"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 border-slate-200 bg-slate-50/50 focus:ring-[#B8860B] focus:border-[#B8860B] rounded-xl text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    placeholder="Masukkan password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-10 border-slate-200 bg-slate-50/50 focus:ring-[#B8860B] focus:border-[#B8860B] rounded-xl text-sm"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#B8860B] hover:bg-[#9A7209] text-white font-semibold rounded-xl transition-all duration-200 text-sm shadow-lg shadow-[#B8860B]/20"
              >
                {loading ? "Memproses..." : "Masuk"}
              </Button>
            </form>

            <p className="text-center text-[10px] text-slate-400 mt-6">
              P3UW &mdash; KPBD Bumi Dipasena
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
